import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Col,
  Row,
  Button,
  Select,
  Tooltip,
  DatePicker,
  InputNumber,
  Popconfirm,
} from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";
import CurrencyInput from "react-currency-input";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const CashAccountAddForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [currencySelect, setCurrencySelect] = useState({
    data: [],
    loading: false,
  });
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [activeBankId, setActiveBankId] = useState(999);
  const [activeTypeId, setActiveTypeId] = useState(0);
  const [form] = Form.useForm();

  useEffect(() => {
    getBanks();
    getCurrencies();
  }, []);

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue(props.data);
    }
  }, [props.data]);

  const getBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });
    RequestHandler.get(
      `/Bank?$select=id,name&$filter=isSupportAccountTransaction eq true&$orderby=name asc`
    ).then((response) => {
      setBankSelect({
        data: response.value,
        loading: false,
      });
    });
  };

  const getCurrencies = () => {
    setCurrencySelect({
      ...currencySelect,
      loading: true,
    });
    RequestHandler.get(`/Currency?$select=id,code,name`).then((response) => {
      setCurrencySelect({
        data: response.value,
        loading: false,
      });
    });
  };

  const onSuccess = () => {
    form.resetFields();

    if (props.onSuccess) props.onSuccess();
  };

  const onFinish = (values) => {
    if (values.dueDate) {
      values.dueDate = values.dueDate.format("YYYY-MM-DD");
    }

    RequestHandler.post("/TenantAccount/AddCashAccount", values).then(
      (response) => {
        setLoading(false);
        if (response.success) {
          onSuccess();
        }
      }
    );
  };

  const onFinishFailed = () => setLoading(false);

  return (
    <Form
      {...formItemLayout}
      validateMessages={defaultValidateMessages}
      form={form}
      name="cashAccountAdd"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Row>
        <Col span={24}>
          <Form.Item
            name="bankId"
            label="Banka"
            initialValue={999}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                option.props.children
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
              placeholder="Seçiniz"
              loading={bankSelect.loading}
              onChange={(value) => setActiveBankId(value)}
            >
              {bankSelect.data.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Hesap Adı" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Hesap Tipi"
            name="type"
            initialValue={0}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Seçiniz"
              onChange={(value) => setActiveTypeId(value)}
            >
              <Select.Option key={0} value={0}>
                Vadesiz
              </Select.Option>
              <Select.Option key={1} value={1}>
                Kredi
              </Select.Option>
              <Select.Option key={2} value={2}>
                Vadeli
              </Select.Option>
            </Select>
          </Form.Item>

          {activeTypeId === 2 && (
            <>
              <Form.Item label="Vade Bitiş Tarihi" name="dueDate">
                <DatePicker
                  allowClear
                  format="DD.MM.YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item label="Vade Faiz Oranı" name="interestRate">
                <InputNumber
                  allowClear
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  decimalSeparator=","
                  precision={2}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="currencyId"
            label="Para Birimi"
            initialValue={1}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                option.props.children
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
              placeholder="Seçiniz"
              loading={currencySelect.loading}
            >
              {currencySelect.data.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {`${item.code} (${item.name})`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="balance"
            label={
              <>
                Bakiye
                <Tooltip
                  title={
                    <>
                      <p>
                        Gireceğiniz bakiye tutarı bugün itibari ile geçerli
                        olacaktır.
                      </p>
                      <p>
                        Eski tarihli elden işlem girişi bakiye hesabında hataya
                        sebep olur.
                      </p>
                    </>
                  }
                >
                  <QuestionCircleOutlined
                    className="gx-text-blue"
                    style={{ marginLeft: 5, fontSize: "120%" }}
                  />
                </Tooltip>
              </>
            }
            rules={[{ required: true, message: "'Bakiye' alanı gerekli." }]}
            initialValue={0}
          >
            <CurrencyInput
              style={{ width: "100%" }}
              className="ant-input"
              decimalSeparator=","
              thousandSeparator="."
              onChange={(value, name) => {
                form.setFieldsValue({
                  balance: name,
                });
              }}
            />
          </Form.Item>

          <Row justify="space-between">
            <Col></Col>
            <Col>
              {activeBankId === 999 ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  htmlType="submit"
                  onClick={() => setLoading(true)}
                  loading={loading}
                >
                  Ekle
                </Button>
              ) : (
                <Popconfirm
                  title="Bankanızdan İşim İçin hesap bilgileri akışı varsa tekrarlı veri olmasına sebep olacaktır. Yine de hesap eklensin mi?"
                  okText="Evet"
                  cancelText="Hayır"
                  onConfirm={() => {
                    setLoading(true);
                    form.submit();
                  }}
                  onCancel={() => setLoading(false)}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    htmlType="submit"
                    onClick={() => setLoading(true)}
                    loading={loading}
                  >
                    Ekle
                  </Button>
                </Popconfirm>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};
export default CashAccountAddForm;
